import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Database, ExternalLink, ArrowLeft } from 'lucide-react';
import { useProjectStore } from '../../store';
import { useContextualNavigation } from '../../hooks/useContextualNavigation';
import { useRegistries } from '../../hooks/queries/useRegistries';

/**
 * RegistriesPage - Dashboard showing all registry-enabled models
 *
 * Displays a summary of all models with registry enabled, including
 * entry counts and quick access to view entries.
 *
 * AC-1409 / AC-1377 P4.3: Migrated from a local `useState` + `useEffect` +
 * `apiService.getRegistryEnabledModels` block to the canonical
 * `useRegistries(projectId)` hook. The cache key parity with
 * `useModelMutations` (AC-1408) means that creating / updating / deleting a
 * model (which already invalidates `queryKeys.projectRegistries`) now
 * automatically refreshes this page without prop drilling. Remounting the
 * page (nav-away-and-back) hits the cache instantly instead of refetching.
 *
 * The project-store `models` slice is intentionally left in place - its
 * removal is tracked in AC-1412 once all consumers are migrated.
 */
const RegistriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentProject } = useProjectStore();
  const { getBackToWorkflow } = useContextualNavigation();
  const backToWorkflow = getBackToWorkflow(currentProject?.project_id);

  // useRegistries owns the projectId-undefined guard: when currentProject is
  // not yet hydrated, the projectId is `undefined` and the hook stays
  // disabled (no fetch fires). This matches the previous
  // `if (currentProject?.project_id) loadRegistryModels()` semantics
  // exactly.
  const registriesQuery = useRegistries(currentProject?.project_id);

  const models = registriesQuery.data ?? [];
  const loading = registriesQuery.isLoading;
  // Mirror the previous string error-message path - the page has no use for
  // the underlying `Error` object, just a flag-string for display.
  const error = registriesQuery.isError ? 'Failed to load registry models' : null;

  const totalEntries = models.reduce((sum, m) => sum + (m.entry_count || 0), 0);

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="animate-pulse">Loading registries...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Back navigation */}
      <div className="mb-4">
        {backToWorkflow ? (
          <Link
            to={backToWorkflow.path}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {backToWorkflow.label}
          </Link>
        ) : currentProject && (
          <Link
            to={`/projects/${currentProject.project_id}`}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Overview
          </Link>
        )}
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Database className="w-8 h-8 text-purple-600" />
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">Registries</h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-cap-dark rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Entries</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-warm-white">{totalEntries}</div>
        </div>
        <div className="bg-white dark:bg-cap-dark rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Registry Models</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-warm-white">{models.length}</div>
        </div>
        <div className="bg-white dark:bg-cap-dark rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Active Registries</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-warm-white">
            {models.filter(m => (m.entry_count || 0) > 0).length}
          </div>
        </div>
      </div>

      {/* Models Table */}
      {models.length === 0 ? (
        <div className="bg-white dark:bg-cap-dark rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
          <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">No Registries Configured</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Enable registry on a model to start tracking outputs.
          </p>
          <button
            onClick={() => navigate(`/projects/${currentProject?.project_id}/schemas`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Go to Models
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-cap-dark rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Dedup Fields
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Entries
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
              {models.map((model) => (
                <tr key={model.model_id} className="hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900 dark:text-warm-white">{model.class_name}</div>
                    {model.description && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">{model.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {model.registry_dedup_fields && model.registry_dedup_fields.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {model.registry_dedup_fields.map((field) => (
                          <span
                            key={field}
                            className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Entire object</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="px-2 py-1 text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full">
                      {model.entry_count || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => navigate(`/projects/${currentProject?.project_id}/schemas?modelId=${model.model_id}&tab=registry&subtab=entries`)}
                      className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                    >
                      View Entries
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};

export default RegistriesPage;
